import { useCallback, useRef, useState } from 'react';
import { PROJECT_STATUSES, STATUS_LABELS, type ProjectStatus } from '../../lib/projectStatus';
import { useDismiss } from '../../lib/useDismiss';
import { ChevronIcon } from '../icons';
import { Button } from '../ui';

function summarise(selected: ProjectStatus[]): string {
  if (selected.length === 0) return 'All statuses';
  if (selected.length === 1) return STATUS_LABELS[selected[0]!];
  return `${selected.length} statuses`;
}

export function StatusFilter({
  selected,
  onChange,
}: {
  selected: ProjectStatus[];
  onChange: (statuses: ProjectStatus[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDismiss(containerRef, open, useCallback(() => setOpen(false), []));

  function toggle(status: ProjectStatus) {
    onChange(
      selected.includes(status)
        ? selected.filter((entry) => entry !== status)
        : [...selected, status],
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <Button aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((value) => !value)}>
        {summarise(selected)}
        <ChevronIcon />
      </Button>

      {open ? (
        <div className="absolute right-0 z-10 mt-1.5 w-56 rounded-xl border border-stone-200 bg-white p-1 shadow-lg">
          {PROJECT_STATUSES.map((status) => (
            <label
              key={status}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-stone-700 hover:bg-stone-50"
            >
              <input
                type="checkbox"
                checked={selected.includes(status)}
                onChange={() => toggle(status)}
                // `form-checkbox` opts this into @tailwindcss/forms. Without the
                // plugin the three classes after it styled nothing at all, and this
                // rendered as raw platform chrome -- an OS-blue checkbox sitting in
                // the middle of a honey UI.
                className="form-checkbox h-4 w-4 rounded border-stone-300 text-honey-600 focus:ring-honey-500 focus:ring-offset-0"
              />
              {STATUS_LABELS[status]}
            </label>
          ))}

          {selected.length > 0 ? (
            <div className="mt-1 border-t border-stone-100 pt-1">
              <Button variant="ghost" onClick={() => onChange([])} className="w-full">
                Clear
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
